import { ChangeEvent } from "react";
import Button from "react-bootstrap/Button";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { formatWorkflowTemplateDetailsTemplate } from "@formatters/workflow-template-details/workflow-template-details-template-formatter";
import {
    AxnAttributeTypeField,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnIdentityProvider,
    AxnRelyingParty,
    AxnServiceOfferingDataSource
} from "@idw-types/data/reference-data/axn";
import {
    ApAssertion,
    Assertion,
    AttributeClassification,
    AttributeLabel,
    AttributeType,
    DeployedPolicyComponent,
    FallbackServiceMapping,
    RpServiceAttributeProperty,
    RpServiceAttributePropertyMap,
    ServiceOffering
} from "@idw-types/data/reference-data/generic";
import { SelectOption } from "@idw-types/common";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { OrganizationSelectFormGroup } from "@containers/common/select-form-groups";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { UpdateTemplateJSONModal } from "@containers/modals/update-template-json-modal/update-template-json-modal";
import { WorkflowTemplateDetailsComponentState } from "../workflow-template-details-component";
import styles from "./workflow-template-metadata-container.less";

type WorkflowTemplateMetadataContainerProps = {
    state: WorkflowTemplateDetailsComponentState;
    setState: (state: WorkflowTemplateDetailsComponentState) => void;
    isDisabled: boolean;
    labelWidth: LabelWidthTypes;
    axnIdentityProviders: Array<AxnIdentityProvider>,
    axnRelyingParties: Array<AxnRelyingParty>,
    axnCompanies: Array<AxnCompany>,
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>,
    attributeTypes: Array<AttributeType>,
    attributeLabels: Array<AttributeLabel>,
    attributeClassifications: Array<AttributeClassification>,
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>,
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>,
    axnuiContent: AXNUIContent,
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    axnAttributeTypeFields: Array<AxnAttributeTypeField>,
    serviceOfferings: Array<ServiceOffering>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    serviceOfferingDataSources: Array<AxnServiceOfferingDataSource>,
    fallbackServiceMappings: Array<FallbackServiceMapping>
};

export const WorkflowTemplateMetadataContainer = (props: WorkflowTemplateMetadataContainerProps) => {
    const modalContext = useModalContext();
    const {
        state,
        isDisabled,
        labelWidth,
        axnIdentityProviders,
        axnRelyingParties,
        axnCompanies,
        axnAvailablePropertyCategories,
        attributeTypes,
        attributeLabels,
        attributeClassifications,
        rpServiceAttributeProperties,
        rpServiceAttributePropertyMaps,
        axnuiContent,
        deployedPolicyComponents,
        axnAttributeTypeFields,
        serviceOfferings,
        apAssertions,
        assertions,
        serviceOfferingDataSources,
        fallbackServiceMappings,
        setState
    } = props;



    const handleOrganizationChange = (newValue: SelectOption) => {
        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                organization: axnCompanies.find(company => company.id === newValue?.value) || null
            },
            selectedOrganizationOption: newValue
        });
    };

    const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                name: event.target.value
            }
        });
    };

    const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                description: event.target.value
            }
        });
    };

    const handleDescriptionURLChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                descriptionURL: event.target.value
            }
        });
    };

    const handleStatusChange = () => {
        const newStatus = state.workflowTemplate.status === EntityStatusTypes.ACTIVE
            ? EntityStatusTypes.INACTIVE
            : EntityStatusTypes.ACTIVE;
        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                status: newStatus
            }
        });
    };

    const updateTemplateString = (newTemplateString: string) => {
        const formattedTemplate = formatWorkflowTemplateDetailsTemplate(
            newTemplateString,
            axnIdentityProviders,
            axnRelyingParties,
            axnCompanies,
            axnAvailablePropertyCategories,
            attributeTypes,
            attributeLabels,
            attributeClassifications,
            rpServiceAttributeProperties,
            rpServiceAttributePropertyMaps,
            axnuiContent,
            deployedPolicyComponents,
            axnAttributeTypeFields,
            serviceOfferings,
            apAssertions,
            assertions,
            serviceOfferingDataSources,
            fallbackServiceMappings
        );

        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                template: formattedTemplate,
                templateString: newTemplateString
            }
        })
    };

    const onUpdateTemplateJSONClick = () => {
        const renderUpdateTemplateJSONModal = (onModalClose: () => void) => {
            return (
                <UpdateTemplateJSONModal
                    onModalClose={onModalClose}
                    templateString={state.workflowTemplate.templateString}
                    updateTemplateString={updateTemplateString}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.UpdateTemplateJSON,
            renderFunction: renderUpdateTemplateJSONModal
        });
    };

    const resource = resources.pages.workflowTemplateDetails.tabs.metadata;

    return (
        <div className={styles.workflowTemplateMetadataContainer}>
                <InputFormGroup
                    controlId="workflow-template-name-input"
                    automationId="workflow-template-name-input"
                    label={resource.inputs.name.label}
                    ariaLabel={resource.inputs.name.label}
                    value={state.workflowTemplate.name || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleNameChange}
                    labelWidth={labelWidth}
                />
                <InputFormGroup
                    controlId="workflow-template-description-input"
                    automationId="workflow-template-description-input"
                    label={resource.inputs.description.label}
                    ariaLabel={resource.inputs.description.label}
                    value={state.workflowTemplate.description || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleDescriptionChange}
                    labelWidth={labelWidth}
                />
                <InputFormGroup
                    controlId="workflow-template-description-url-input"
                    automationId="workflow-template-name-input"
                    label={resource.inputs.descriptionURL.label}
                    ariaLabel={resource.inputs.descriptionURL.label}
                    value={state.workflowTemplate.descriptionURL || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleDescriptionURLChange}
                    labelWidth={labelWidth}
                />
                <OrganizationSelectFormGroup
                    idPrefix="workflow-template"
                    onChange={handleOrganizationChange}
                    axnCompanies={axnCompanies}
                    selectedOption={state.selectedOrganizationOption}
                    disabled={isDisabled}
                    isRequired={false}
                    isSearchable={true}
                    isClearable={true}
                    labelWidth={labelWidth}
                />
                <TextareaFormGroup
                    controlId="workflow-template-template-json-input"
                    automationId="workflow-template-template-json-input"
                    label={resource.inputs.templateJSON.label}
                    ariaLabel={resource.inputs.templateJSON.label}
                    value={state.workflowTemplate.templateString || ""}
                    disabled={true}
                    isRequired={true}
                    onChange={() => { }}
                    labelWidth={labelWidth}
                />
                <div className={styles.buttonDiv}>
                    <Button id="workflow-template-update-template-json-button" disabled={isDisabled} onClick={onUpdateTemplateJSONClick}>
                        {resource.buttons.updateTemplateJSON.label}
                    </Button>
                </div>
                <ToggleFormGroup
                    controlId={"workflow-template-status-toggle"}
                    automationId={"workflow-template-status-toggle"}
                    label={resource.inputs.status.label}
                    ariaLabel={resource.inputs.status.label}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    labelWidth={labelWidth}
                    checked={state.workflowTemplate.status === EntityStatusTypes.ACTIVE}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleStatusChange}
                    value={`${state.workflowTemplate.status}`}
                />
        </div>
    );
};